import { faFilter, faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/base/Button";
import MeetingCard from "components/cards/MeetingCard";
import NoDataFound from "components/common/NoDataFound";
import SearchBox from "components/common/SearchBox";
import axiosInstance from "helpers/api";
import { convertToISO8601 } from "helpers/utils";
import { useAtom } from "jotai";
import { dealDetailsMeetingsAtom } from "jotai/DealJotaiInstance";
import { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Modal } from "react-bootstrap";
import { useForm, SubmitHandler } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";

type Inputs = {
  topic: string;
  start: string;
  end: string;
  location: string;
};

const DealDetailsMeeting = ({ meetings, handleSearchMeeting }: any) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<Inputs>();
  const [open, setOpen] = useState(false);
  const [meetingList, setMeetingList] = useState([]);
  const [minDateTime, setMinDateTime] = useState("");
  const [, setDealDetailsMeeting] = useAtom(dealDetailsMeetingsAtom);
  let { id } = useParams();

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      const response = await axiosInstance.post(`/deals/${id}/meetings`, {
        meeting_name: data.topic,
        meeting_start: convertToISO8601(data.start),
        meeting_end: convertToISO8601(data.end),
        meeting_location: data.location,
      });
      setMeetingList(response.data.data.meetings);
      setDealDetailsMeeting(response.data.data.meetings);
      toast.success(response.data.message);
      reset();
      setOpen(false);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  useEffect(() => {
    setMeetingList(meetings);
  }, [meetings]);

  useEffect(() => {
    const now = new Date();
    const localISOTime = new Date(
      now.getTime() - now.getTimezoneOffset() * 60000
    )
      .toISOString()
      .slice(0, 16);
    setMinDateTime(localISOTime);
  }, []);

  const handleDateChange = (event: any) => {
    const endDate = watch("end");
    if (!endDate) {
      let [datePart, timePart] = event.target.value.split("T");
      let [hour, minute] = timePart.split(":").map(Number);
      hour = (hour + 1) % 24;
      const updatedTime = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      const updatedDateTime = `${datePart}T${updatedTime}`;
      setValue("end", updatedDateTime);
    }
  };

  return (
    <>
      <h2 className="mb-4">Meeting</h2>
      <Row className="align-items-center g-2 flex-wrap mb-3">
        <Col sm="auto">
          <SearchBox
            placeholder="Search meeting"
            className="mb-2 mb-sm-0"
            onChange={handleSearchMeeting}
          />
        </Col>
        <Col xs="auto" className="flex-md-grow-1">
          <div className="d-flex">
            <p className="mb-0 fs-9 text-body-tertiary fw-bold">
              <FontAwesomeIcon
                icon={faFilter}
                className="me-1 fw-extra-bold fs-10"
              />
              {meetingList.length} Meetings
            </p>
          </div>
        </Col>
        <Col xs="auto">
          <Button
            variant="primary"
            onClick={() => setOpen(true)}
            startIcon={<FontAwesomeIcon icon={faPlus} />}
          >
            Add Meeting
          </Button>
        </Col>
      </Row>
      <Row className="g-3">
        {meetingList.length > 0 ? (
          meetingList.map((meeting: any) => (
            <Col xxl={6} key={meeting.id}>
              <MeetingCard meeting={meeting} />
            </Col>
          ))
        ) : (
          <NoDataFound type="Meetings" customClass="mt-lg-10" />
        )}
      </Row>

      <Modal
        show={open}
        onHide={() => setOpen(false)}
        className="p-0"
        centered
        contentClassName="border"
      >
        <Modal.Header className="border-translucent p-4">
          <h5 className="modal-title text-body-highlight fs-6 lh-sm">
            Add Meeting
          </h5>
          <Button className="p-1 ms-auto" onClick={() => setOpen(false)}>
            <FontAwesomeIcon icon={faTimes} className="fs-7 text-danger" />
          </Button>
        </Modal.Header>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body className="pt-4 pb-2 px-4">
            <div className="mb-3">
              <Form.Group>
                <Form.Label className="form-label-header mb-2">
                  Topic
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Topic"
                  {...register("topic", {
                    required: true,
                    maxLength: 100,
                  })}
                />
              </Form.Group>
              {errors.topic?.type === "required" && (
                <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                  Topic is required
                </p>
              )}
            </div>
            <div className="mb-3">
              <Form.Group>
                <Form.Label className="form-label-header mb-2">
                  Location
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Location"
                  {...register("location")}
                />
              </Form.Group>
            </div>
            <Row className="g-3 mb-3">
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="form-label-header mb-2">
                    Start Time
                  </Form.Label>
                  <Form.Control
                    min={minDateTime}
                    type="datetime-local"
                    placeholder="Enter First Name"
                    {...register("start", {
                      required: true,
                      maxLength: 100,
                    })}
                    onChange={(e) => {
                      handleDateChange(e);
                      register("start").onChange(e); // Ensures React Hook Form handles the change
                    }}
                  />
                </Form.Group>
                {errors.start?.type === "required" && (
                  <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                    Start Time is required
                  </p>
                )}
              </Col>

              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="form-label-header mb-2">
                    End Time
                  </Form.Label>
                  <Form.Control
                    min={minDateTime}
                    type="datetime-local"
                    placeholder="Enter First Name"
                    {...register("end", {
                      required: true,
                      maxLength: 100,
                    })}
                  />
                </Form.Group>
                {errors.end?.type === "required" && (
                  <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                    End Time is required
                  </p>
                )}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-end align-items-center px-4 pb-4 border-0 pt-3">
            <Button
              variant="phoenix-primary"
              size="sm"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              className="fs-9 my-0"
            >
              Add
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default DealDetailsMeeting;
